// Core imports
import { Camera, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";

// Components
import { BuildTimestamp } from "./components/BuildTimestamp";
import { GenerateButton } from "./components/GenerateButton";
import { PhotoPreview } from "./components/PhotoPreview";
import { PhotoUploader } from "./components/PhotoUploader";

// Store
import { useArticleStore } from "./store/useArticleStore";
import { useDraftPersistence } from "./hooks/useDraftPersistence";

const Article = lazy(async () => {
  const module = await import("./components/Article");
  return { default: module.Article };
});

function App() {
  const { article, reset } = useArticleStore();
  const [isInputAreaFolded, setIsInputAreaFolded] = useState(false);
  const [hasGeneratedArticle, setHasGeneratedArticle] = useState(false);
  useDraftPersistence();

  useEffect(() => {
    // Check if article has sections with descriptions (meaning generation is complete)
    const hasCompletedSections = article.sections.some((section) => section.description);

    if (hasCompletedSections && !hasGeneratedArticle) {
      setIsInputAreaFolded(true);
      setHasGeneratedArticle(true);
    }
  }, [article.sections, hasGeneratedArticle]);

  const handleStartOver = () => {
    if (!window.confirm("Start a new journal? This clears the local draft and uploaded photos.")) {
      return;
    }
    reset();
    setIsInputAreaFolded(false);
    setHasGeneratedArticle(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {article.sections.length > 0 && (
          <div className="flex items-center gap-2 rounded-t-xl bg-white p-2 shadow-sm no-print">
            <button
              type="button"
              aria-expanded={!isInputAreaFolded}
              onClick={() => setIsInputAreaFolded((prev) => !prev)}
              className="flex flex-1 items-center justify-between rounded-lg p-2 text-sm text-gray-600 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <span>{isInputAreaFolded ? "Show" : "Hide"} input area</span>
              {isInputAreaFolded ? (
                <ChevronDown aria-hidden="true" className="h-4 w-4" />
              ) : (
                <ChevronUp aria-hidden="true" className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={handleStartOver}
              className="inline-flex items-center gap-1.5 rounded-lg p-2 text-sm text-gray-600 hover:bg-red-50 hover:text-red-700 focus-visible:ring-2 focus-visible:ring-red-500"
            >
              <RotateCcw aria-hidden="true" className="h-4 w-4" />
              <span className="hidden sm:inline">Start over</span>
              <span className="sr-only sm:hidden">Start over</span>
            </button>
          </div>
        )}

        {(!article.sections.length || !isInputAreaFolded) && (
          <>
            <div className="text-center mb-8 no-print">
              <Camera className="h-12 w-12 text-blue-500 mx-auto" />
              <h1 className="mt-4 text-3xl font-bold text-gray-900">Travel Photo Explainer</h1>
              <p className="mt-2 text-gray-600">
                Upload your travel photos to learn about your trip
              </p>
            </div>

            <div className="bg-white rounded-b-xl shadow-sm p-6 mb-8 no-print">
              <PhotoPreview />
              <PhotoUploader />

              {article.sections.length > 0 && (
                <div className="mt-6">
                  <div className="flex flex-col gap-4">
                    <GenerateButton />
                  </div>
                  {article.error && (
                    <p role="alert" className="text-red-600 text-sm mt-2">
                      {article.error}
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {article.sections.length > 0 && (
          <Suspense fallback={null}>
            <Article />
          </Suspense>
        )}
      </div>
      <footer className="text-center text-sm text-gray-500 py-8 border-t border-gray-100 print:hidden">
        <div className="container mx-auto px-4 space-y-4">
          <div className="space-y-1">
            <p>
              Made with ❤️ by{" "}
              <a
                href="https://osteele.com"
                className="text-emerald-600 hover:text-emerald-700 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Oliver Steele
              </a>
            </p>
            <p className="text-xs text-gray-400">
              <BuildTimestamp />
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
